import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiDelete, operationsClient } from '@/lib/api/client';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import type { AccountingExclusion, AccountingExclusionsList } from '@/types/accounting-sync';

export function useAccountingExclusions(filter?: {
  entityType?: string;
  limit?: number;
  cursor?: string | null;
}) {
  const search = new URLSearchParams();
  if (filter?.entityType) search.set('entityType', filter.entityType);
  if (filter?.limit != null) search.set('limit', String(filter.limit));
  if (filter?.cursor) search.set('cursor', filter.cursor);
  const qs = search.toString();
  const url = settingsEndpoints.accountingSyncExclusions() + (qs ? `?${qs}` : '');

  return useQuery({
    queryKey: [
      ...settingsKeys.accountingExclusions(),
      filter?.entityType ?? 'all',
      filter?.limit ?? 'no-limit',
      filter?.cursor ?? 'first',
    ],
    queryFn: () => apiGet<AccountingExclusionsList>(operationsClient, url),
    staleTime: 30_000,
  });
}

export function useAddAccountingExclusion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      entityType: string;
      entityId: string;
      provider?: string;
      reason?: string;
    }) =>
      apiPost<AccountingExclusion, typeof input>(
        operationsClient,
        settingsEndpoints.accountingSyncExclusions(),
        input,
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: settingsKeys.accountingExclusions() });
      qc.invalidateQueries({
        queryKey: settingsKeys.accountingEntity(vars.entityType, vars.entityId),
      });
      qc.invalidateQueries({ queryKey: settingsKeys.accountingDashboard() });
    },
  });
}

export function useRemoveAccountingExclusion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { entityType: string; entityId: string; provider?: string }) =>
      apiDelete<void>(
        operationsClient,
        settingsEndpoints.accountingSyncRemoveExclusion(input.entityType, input.entityId) +
          (input.provider ? `?provider=${encodeURIComponent(input.provider)}` : ''),
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: settingsKeys.accountingExclusions() });
      qc.invalidateQueries({
        queryKey: settingsKeys.accountingEntity(vars.entityType, vars.entityId),
      });
      qc.invalidateQueries({ queryKey: settingsKeys.accountingDashboard() });
    },
  });
}
